// src/components/settings/CountryManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { settingsService } from '../../services/settingsService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';

const CountryManagement = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCountry, setEditingCountry] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', dial_code: '' });
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    const loadCountries = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current_page,
                limit: pagination.per_page,
                search: searchTerm
            };
            const response = await settingsService.getCountries(params);
            setCountries(response.countries || []);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.current_page, pagination.per_page, searchTerm]);

    useEffect(() => {
        loadCountries();
    }, [loadCountries]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const handleAddClick = () => {
        setEditingCountry(null);
        setFormData({ name: '', code: '', dial_code: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (country) => {
        setEditingCountry(country);
        setFormData({
            name: country.name || '',
            code: country.code || '',
            dial_code: country.dial_code || ''
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Country name is required';
        if (!formData.code.trim()) errors.code = 'Country code is required';
        if (formData.code.trim().length !== 2) errors.code = 'Country code must be 2 characters';
        if (!formData.dial_code.trim()) errors.dial_code = 'Dial code is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            if (editingCountry) {
                await settingsService.updateCountry(editingCountry.id, formData);
                showToast('Country updated successfully', 'success');
            } else {
                await settingsService.createCountry(formData);
                showToast('Country created successfully', 'success');
            }
            setShowModal(false);
            loadCountries();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this country?')) return;

        try {
            await settingsService.deleteCountry(id);
            showToast('Country deleted successfully', 'success');
            loadCountries();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        Country Management
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        Manage countries in the system
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add Country
                </button>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '10px 15px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                    }}
                />
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : countries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-globe" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No countries found</h3>
                    <p>Try adjusting your search or add a new country</p>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Name</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Code</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Dial Code</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {countries.map((country) => (
                                    <tr key={country.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{country.id}</td>
                                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{country.name}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{country.code}</td>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>{country.dial_code}</td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleEditClick(country)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#007bff',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    marginRight: '8px'
                                                }}
                                            >
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(country.id)}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#dc3545',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ marginTop: '20px' }}>
                        <Pagination
                            currentPage={pagination.current_page}
                            totalPages={pagination.total_pages}
                            onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                        />
                    </div>
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingCountry ? 'Edit Country' : 'Add Country'}
                size="medium"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Country Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.name ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Enter country name"
                        />
                        {formErrors.name && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.name}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Country Code (2 letters) <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            maxLength="2"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.code ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                textTransform: 'uppercase'
                            }}
                            placeholder="e.g., US, PK"
                        />
                        {formErrors.code && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.code}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Dial Code <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="dial_code"
                            value={formData.dial_code}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.dial_code ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="e.g., +1, +92"
                        />
                        {formErrors.dial_code && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.dial_code}</div>}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default CountryManagement;
